import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ClipboardList, Sparkles, ChevronRight, ChevronLeft, Shield, Brain, Target, BarChart3 } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from "recharts";
import { trainingGoalOptions, exerciseTypeOptions } from "./constants";

const APSQ_DOMAINS = [
  {
    title: "Self-Regulation",
    color: "from-blue-400 to-blue-600",
    questions: [
      { key: 'apsq_q1', q: '1. I have felt so stressed that I was unable to handle things' },
      { key: 'apsq_q2', q: '2. I have felt that I could not stop worrying about things related to my sport' },
      { key: 'apsq_q3', q: '3. I have felt that I could not control the important things in my sporting life' },
      { key: 'apsq_q4', q: '4. I have felt overwhelmed by difficulties in my sport and could not overcome them' },
    ]
  },
  {
    title: "Performance Concerns",
    color: "from-violet-400 to-violet-600",
    questions: [
      { key: 'apsq_q5', q: '5. I have felt that I have not been performing up to my potential' },
      { key: 'apsq_q6', q: '6. I have felt that the demands of my sport were more than I could handle' },
      { key: 'apsq_q7', q: '7. I have had difficulties with sleep because of my sport concerns' },
      { key: 'apsq_q8', q: '8. I have felt worn out by the demands of my sport' },
    ]
  },
  {
    title: "External Coping",
    color: "from-rose-400 to-rose-600",
    questions: [
      { key: 'apsq_q9', q: '9. I have used alcohol or other substances to cope with the pressures of my sport' },
      { key: 'apsq_q10', q: '10. I have avoided people or situations related to my sport' },
    ]
  }
];

const ACSI_DOMAINS = [
  {
    title: "Coping with Adversity",
    color: "from-sky-400 to-sky-600",
    icon: Shield,
    description: "How you respond when things go wrong in training or competition",
    questions: [
      { key: 'acsi_q1', q: 'I remain positive and enthusiastic during competition, no matter how badly things are going' },
      { key: 'acsi_q2', q: 'When things are going badly, I tell myself to keep calm, and this works for me' },
      { key: 'acsi_q3', q: 'I maintain emotional control no matter how things are going for me in competition' },
      { key: 'acsi_q4', q: 'I handle unexpected situations in my sport very well' },
    ]
  },
  {
    title: "Peaking Under Pressure",
    color: "from-amber-400 to-amber-600",
    icon: Target,
    description: "Your ability to perform your best when it matters most",
    questions: [
      { key: 'acsi_q5', q: 'I perform well under the pressure of competition' },
      { key: 'acsi_q6', q: 'I make fewer mistakes when the pressure is on because I concentrate better' },
      { key: 'acsi_q7', q: 'The more pressure there is during a game, the more I enjoy it' },
      { key: 'acsi_q8', q: 'I feel confident that I will play well in pressure situations' },
    ]
  },
  {
    title: "Goal Setting & Mental Preparation",
    color: "from-emerald-400 to-emerald-600",
    icon: ClipboardList,
    description: "How you set goals and mentally prepare for performance",
    questions: [
      { key: 'acsi_q9', q: 'On a daily or weekly basis, I set very specific goals for myself that guide what I do' },
      { key: 'acsi_q10', q: 'I have my own game plan worked out in my head long before the game begins' },
      { key: 'acsi_q11', q: 'I set my own performance goals for each practice' },
      { key: 'acsi_q12', q: 'I have a specific plan for how I want to perform in each competition' },
    ]
  },
  {
    title: "Concentration",
    color: "from-indigo-400 to-indigo-600",
    icon: Brain,
    description: "Your ability to focus and block out distractions",
    questions: [
      { key: 'acsi_q13', q: 'It is easy for me to keep distracting thoughts from interfering with something I am doing' },
      { key: 'acsi_q14', q: 'I can focus my attention and block out distractions when I need to' },
      { key: 'acsi_q15', q: 'I am good at focusing my efforts and blocking out distractions during competition' },
      { key: 'acsi_q16', q: 'I am able to stay focused on the task at hand despite unexpected situations' },
    ]
  },
  {
    title: "Freedom from Worry",
    color: "from-teal-400 to-teal-600",
    icon: Shield,
    description: "Your ability to avoid putting unnecessary pressure on yourself",
    questions: [
      { key: 'acsi_q17', q: 'I do not worry about performing poorly' },
      { key: 'acsi_q18', q: 'I do not put a lot of pressure on myself by worrying about how I will perform' },
      { key: 'acsi_q19', q: 'While competing, I do not worry about making mistakes' },
      { key: 'acsi_q20', q: 'I rarely worry about the outcome of competition' },
    ]
  },
  {
    title: "Confidence & Achievement Motivation",
    color: "from-orange-400 to-orange-600",
    icon: Target,
    description: "Your self-belief and drive to succeed",
    questions: [
      { key: 'acsi_q21', q: 'I am confident I can perform up to my potential' },
      { key: 'acsi_q22', q: 'I feel confident that I will meet my challenges successfully' },
      { key: 'acsi_q23', q: 'I get the most out of my talent and skills' },
      { key: 'acsi_q24', q: 'When I set a goal for myself, I work hard to achieve it' },
    ]
  },
  {
    title: "Coachability",
    color: "from-pink-400 to-pink-600",
    icon: Brain,
    description: "Your openness to learning and responding to coaching",
    questions: [
      { key: 'acsi_q25', q: 'I accept constructive criticism from my coach without taking it personally' },
      { key: 'acsi_q26', q: "I improve my skills by listening carefully to advice and instruction from coaches" },
      { key: 'acsi_q27', q: "When a coach tells me how to correct a mistake, I tend to take it positively" },
      { key: 'acsi_q28', q: "I am open to learning new ways to do things in my sport" },
    ]
  }
];

const APSQ_SCALE_COLORS = ['bg-emerald-500', 'bg-lime-500', 'bg-amber-500', 'bg-orange-500', 'bg-red-500'];
const ACSI_SCALE_LABELS = ['Almost Never', 'Sometimes', 'Often', 'Almost Always'];
const ACSI_SCALE_COLORS = ['bg-gray-400', 'bg-sky-500', 'bg-emerald-500', 'bg-emerald-700'];

const STEPS = [
  { id: 'apsq', label: 'Psychological Strain', shortLabel: 'APSQ', icon: Shield },
  { id: 'acsi', label: 'Coping Skills', shortLabel: 'ACSI', icon: Brain },
  { id: 'profile', label: 'Your Profile', shortLabel: 'Profile', icon: BarChart3 },
  { id: 'preferences', label: 'Goals & Preferences', shortLabel: 'Goals', icon: Target },
];

export function APSQAssessmentDialog({
  open, onOpenChange, assessmentData, loading,
  onAssessmentChange, onToggleArrayValue, onSubmit
}) {
  const [step, setStep] = useState(0);

  const handleOpenChange = (isOpen) => {
    if (!isOpen) setStep(0);
    onOpenChange(isOpen);
  };

  const acsiProfile = useMemo(() => {
    const subscales = [
      { name: 'Coping w/\nAdversity', short: 'Adversity', keys: ['acsi_q1','acsi_q2','acsi_q3','acsi_q4'] },
      { name: 'Peaking Under\nPressure', short: 'Pressure', keys: ['acsi_q5','acsi_q6','acsi_q7','acsi_q8'] },
      { name: 'Goal Setting', short: 'Goals', keys: ['acsi_q9','acsi_q10','acsi_q11','acsi_q12'] },
      { name: 'Concentration', short: 'Focus', keys: ['acsi_q13','acsi_q14','acsi_q15','acsi_q16'] },
      { name: 'Freedom from\nWorry', short: 'Worry-Free', keys: ['acsi_q17','acsi_q18','acsi_q19','acsi_q20'] },
      { name: 'Confidence &\nMotivation', short: 'Confidence', keys: ['acsi_q21','acsi_q22','acsi_q23','acsi_q24'] },
      { name: 'Coachability', short: 'Coachability', keys: ['acsi_q25','acsi_q26','acsi_q27','acsi_q28'] },
    ];
    return subscales.map(s => {
      const score = s.keys.reduce((sum, k) => sum + (assessmentData[k] || 0), 0);
      const pct = Math.round((score / 12) * 100);
      const level = score <= 4 ? 'Needs Work' : score <= 7 ? 'Developing' : score <= 10 ? 'Good' : 'Excellent';
      const color = score <= 4 ? 'text-red-600 bg-red-50 border-red-200' : score <= 7 ? 'text-amber-600 bg-amber-50 border-amber-200' : score <= 10 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : 'text-emerald-700 bg-emerald-100 border-emerald-300';
      return { subject: s.short, fullName: s.name, score, max: 12, pct, level, color, fullMark: 12 };
    });
  }, [assessmentData]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
              <ClipboardList className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="block text-lg font-bold text-gray-900">Comprehensive Athlete Assessment</span>
              <span className="block text-xs font-medium text-gray-400 mt-0.5">APSQ + ACSI-28 Sport Coping Skills Inventory</span>
            </div>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-2">
            Complete both assessments for a highly personalized training plan targeting your psychological strain areas and developing your coping skills.
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex-shrink-0 flex items-center gap-1 px-1 py-3 border-b border-gray-100">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isPast = i < step;
            return (
              <button
                key={s.id}
                data-testid={`step-${s.id}`}
                onClick={() => setStep(i)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300'
                    : isPast
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'text-gray-400 hover:bg-gray-50'
                }`}
              >
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  isActive ? 'bg-emerald-600 text-white' : isPast ? 'bg-emerald-400 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {isPast ? '✓' : i + 1}
                </div>
                <Icon className="w-3.5 h-3.5 hidden sm:block" />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.shortLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-4 space-y-6 px-1">
          {/* Step 1: APSQ */}
          {step === 0 && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                Rate each statement based on your experience <strong>over the past month</strong>. (1 = None of the time, 5 = All of the time)
              </div>
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3 text-[11px] font-semibold text-gray-500 border border-gray-100">
                <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px]">1</span> Never</span>
                <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-lime-100 text-lime-700 flex items-center justify-center text-[10px]">2</span> Rarely</span>
                <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px]">3</span> Sometimes</span>
                <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-[10px]">4</span> Often</span>
                <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px]">5</span> Always</span>
              </div>
              {APSQ_DOMAINS.map((domain) => (
                <div key={domain.title}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-1.5 h-6 rounded-full bg-gradient-to-b ${domain.color}`} />
                    <h3 className="text-sm font-bold text-gray-800">{domain.title}</h3>
                  </div>
                  <div className="space-y-3">
                    {domain.questions.map(item => (
                      <div key={item.key} data-testid={`apsq-${item.key}`} className="bg-white rounded-xl border border-gray-100 p-4">
                        <p className="text-sm text-gray-700 mb-3 leading-relaxed">{item.q}</p>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(val => {
                            const isSelected = assessmentData[item.key] === val;
                            return (
                              <button
                                key={val}
                                data-testid={`${item.key}-val-${val}`}
                                onClick={() => onAssessmentChange(item.key, val)}
                                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
                                  isSelected
                                    ? `${APSQ_SCALE_COLORS[val - 1]} text-white shadow-sm scale-105`
                                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100 border border-gray-100'
                                }`}
                              >
                                {val}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Step 2: ACSI */}
          {step === 1 && (
            <>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
                Rate how well each statement describes <strong>your current sport behavior</strong>. This helps identify your coping strengths and areas for development.
              </div>
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3 text-[11px] font-semibold text-gray-500 border border-gray-100">
                {ACSI_SCALE_LABELS.map((label, i) => (
                  <span key={label} className="flex items-center gap-1">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white ${ACSI_SCALE_COLORS[i]}`}>{i}</span>
                    {label}
                  </span>
                ))}
              </div>
              {ACSI_DOMAINS.map((domain) => {
                const Icon = domain.icon;
                return (
                  <div key={domain.title}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-1.5 h-6 rounded-full bg-gradient-to-b ${domain.color}`} />
                      <Icon className="w-4 h-4 text-gray-600" />
                      <h3 className="text-sm font-bold text-gray-800">{domain.title}</h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-3 ml-6">{domain.description}</p>
                    <div className="space-y-3">
                      {domain.questions.map(item => (
                        <div key={item.key} data-testid={`acsi-${item.key}`} className="bg-white rounded-xl border border-gray-100 p-4">
                          <p className="text-sm text-gray-700 mb-3 leading-relaxed">{item.q}</p>
                          <div className="flex gap-2">
                            {[0, 1, 2, 3].map(val => {
                              const isSelected = assessmentData[item.key] === val;
                              return (
                                <button
                                  key={val}
                                  data-testid={`${item.key}-val-${val}`}
                                  onClick={() => onAssessmentChange(item.key, val)}
                                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${
                                    isSelected
                                      ? `${ACSI_SCALE_COLORS[val]} text-white shadow-sm scale-105`
                                      : 'bg-gray-50 text-gray-400 hover:bg-gray-100 border border-gray-100'
                                  }`}
                                >
                                  {val}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Step 3: Coping Skills Profile */}
          {step === 2 && (
            <>
              <div className="bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
                Here's your <strong>Coping Skills Profile</strong> based on your ACSI responses. Lower scores highlight areas your training plan will prioritize.
              </div>

              <div data-testid="acsi-radar-chart" className="bg-white rounded-xl border border-gray-100 p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart cx="50%" cy="50%" outerRadius="75%" data={acsiProfile}>
                    <PolarGrid stroke="#d1d5db" />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }}
                    />
                    <PolarRadiusAxis
                      angle={90} domain={[0, 12]}
                      tick={{ fontSize: 9, fill: '#9ca3af' }}
                      tickCount={4}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 text-xs">
                            <p className="font-bold text-gray-900">{d.subject}</p>
                            <p className="text-gray-600">Score: {d.score} / {d.max} ({d.pct}%)</p>
                            <p className={`font-semibold ${d.score <= 4 ? 'text-red-600' : d.score <= 7 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {d.level}
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Radar
                      name="Your Profile"
                      dataKey="score"
                      stroke="#059669"
                      fill="#10b981"
                      fillOpacity={0.25}
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#059669', strokeWidth: 0 }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {acsiProfile.map(s => (
                  <div key={s.subject} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs font-medium ${s.color}`}>
                    <span>{s.subject}</span>
                    <span className="font-bold">{s.score}/12 &middot; {s.level}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                <strong>Legend:</strong>
                <span className="ml-2 text-red-600">0-4 Needs Work</span> &middot;
                <span className="ml-1 text-amber-600">5-7 Developing</span> &middot;
                <span className="ml-1 text-emerald-600">8-10 Good</span> &middot;
                <span className="ml-1 text-emerald-700">11-12 Excellent</span>
              </div>
            </>
          )}

          {/* Step 4: Preferences */}
          {step === 3 && (
            <>
              {/* Sport & Position */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Your Sport & Position</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Sport</Label>
                    <input
                      data-testid="assessment-sport"
                      type="text"
                      value={assessmentData.sport || ''}
                      onChange={(e) => onAssessmentChange('sport', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      placeholder="e.g. Basketball, Soccer"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Position / Role</Label>
                    <input
                      data-testid="assessment-position"
                      type="text"
                      value={assessmentData.position || ''}
                      onChange={(e) => onAssessmentChange('position', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                      placeholder="e.g. Point Guard, Striker"
                    />
                  </div>
                </div>
              </div>

              {/* Competition Level */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Competition Level</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {['Youth', 'High School', 'College', 'Professional'].map(level => (
                    <button
                      key={level}
                      data-testid={`level-${level.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => onAssessmentChange('competition_level', level)}
                      className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${
                        assessmentData.competition_level === level
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm'
                          : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Primary Goal - Free Text */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Primary Goal for This Plan</Label>
                <textarea
                  data-testid="assessment-primary-goal"
                  value={assessmentData.primary_goal || ''}
                  onChange={(e) => onAssessmentChange('primary_goal', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                  placeholder="Describe your main goal, e.g. 'Stay focused during high-pressure moments in 4th quarter'"
                />
              </div>

              {/* What to improve - existing */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">What do you want to improve?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {trainingGoalOptions.map((goal) => (
                    <button
                      key={goal}
                      data-testid={`goal-${goal.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => onToggleArrayValue('training_goals', goal)}
                      className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${
                        (assessmentData.training_goals || []).includes(goal)
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm'
                          : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preferred Exercise Types - existing */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Preferred Exercise Types</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {exerciseTypeOptions.map((type) => (
                    <button
                      key={type}
                      data-testid={`exercise-${type.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => onToggleArrayValue('preferred_exercise_types', type)}
                      className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${
                        (assessmentData.preferred_exercise_types || []).includes(type)
                          ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                          : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Training Frequency */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Preferred Training Frequency</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {['3x/week', '4x/week', '5x/week', 'Daily'].map(freq => (
                    <button
                      key={freq}
                      data-testid={`freq-${freq.replace(/\//g, '-')}`}
                      onClick={() => onAssessmentChange('training_frequency', freq)}
                      className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${
                        assessmentData.training_frequency === freq
                          ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                          : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>

              {/* Available Time per Session - existing */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Available Time per Session</Label>
                <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 p-4">
                  <span className="text-xs text-gray-400">5 min</span>
                  <input
                    type="range" min="5" max="120" step="5"
                    value={assessmentData.time_availability_minutes}
                    onChange={(e) => onAssessmentChange('time_availability_minutes', parseInt(e.target.value))}
                    className="flex-1 accent-emerald-600"
                    data-testid="time-availability-slider"
                  />
                  <span className="text-xs text-gray-400">120 min</span>
                  <span className="text-sm font-bold text-emerald-600 w-16 text-right">{assessmentData.time_availability_minutes} min</span>
                </div>
              </div>

              {/* Upcoming Event / Competition */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Upcoming Competition or Event</Label>
                <input
                  data-testid="assessment-upcoming-event"
                  type="text"
                  value={assessmentData.upcoming_event || ''}
                  onChange={(e) => onAssessmentChange('upcoming_event', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="e.g. State Championship - March 15th"
                />
              </div>

              {/* Specific Challenges */}
              <div>
                <Label className="text-sm font-bold text-gray-800 mb-2 block">Specific Challenges or Concerns</Label>
                <textarea
                  data-testid="assessment-challenges"
                  value={assessmentData.specific_challenges || ''}
                  onChange={(e) => onAssessmentChange('specific_challenges', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                  placeholder="Describe any specific mental performance challenges you're facing..."
                />
              </div>
            </>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t border-gray-100">
          <Button
            onClick={() => step === 0 ? handleOpenChange(false) : setStep(step - 1)}
            variant="outline" className="text-sm"
          >
            {step === 0 ? 'Cancel' : <><ChevronLeft className="w-4 h-4 mr-1" /> Back</>}
          </Button>

          <span className="text-xs text-gray-400">Step {step + 1} of {STEPS.length}</span>

          {step < STEPS.length - 1 ? (
            <Button
              data-testid="next-step-btn"
              onClick={() => setStep(step + 1)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="submit-assessment-btn"
              onClick={onSubmit}
              disabled={loading}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold shadow-sm"
            >
              {loading ? "Generating Your Plan..." : "Generate Plan"}
              <Sparkles className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
